import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Sparkles, Star, Zap, Info, X } from 'lucide-react';
import { 
  getMatchingActivities, 
  getCategoryById, 
  ACTIVITY_CATEGORIES 
} from '../data/universalDayActivities';

/**
 * Componente que muestra actividades sugeridas basadas en los números del día
 * con sistema de "matches" (coincidencias) inteligente.
 */
export default function ActivitySuggestions({ 
  universalDay, 
  personalDay, 
  universalMonth, 
  universalWeek,
  isExpanded: initialExpanded = false 
}) {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  // Obtener actividades que coinciden
  const matches = useMemo(() => {
    return getMatchingActivities(universalDay, personalDay, universalMonth, universalWeek);
  }, [universalDay, personalDay, universalMonth, universalWeek]);

  // Si no hay matches, no mostrar nada
  if (matches.total === 0) return null;

  // Agrupar singles por categoría para mostrar más organizado
  const singlesByCategory = useMemo(() => {
    const grouped = {};
    matches.singleMatch.forEach(act => {
      if (!grouped[act.category]) {
        grouped[act.category] = [];
      }
      grouped[act.category].push(act);
    });
    return grouped;
  }, [matches.singleMatch]);

  const renderMatchBadge = (matchTypes) => {
    return (
      <div className="flex gap-1 flex-wrap">
        {matchTypes.includes('día') && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-morado/10 text-morado">
            Día {universalDay}
          </span>
        )}
        {matchTypes.includes('personal') && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-700">
            Personal
          </span>
        )}
        {matchTypes.includes('mes') && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
            Mes {universalMonth}
          </span>
        )}
        {matchTypes.includes('semana') && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
            Semana
          </span>
        )}
        {matchTypes.includes('maestro') && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
            Maestro
          </span>
        )}
      </div>
    );
  };

  const renderActivityItem = (activity, showBadges = true) => {
    const category = getCategoryById(activity.category);
    
    return (
      <motion.button
        key={activity.id}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        className="w-full text-left p-3 rounded-xl bg-white/80 hover:bg-white border border-gray-100 hover:border-[#d4af37]/50 transition-all group"
        onClick={() => setSelectedActivity(activity)}
      >
        <div className="flex items-start gap-3">
          <span className="text-xl">{category?.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-800 text-sm leading-tight group-hover:text-morado transition-colors">
              {activity.name}
            </div>
            {showBadges && activity.matchTypes && (
              <div className="mt-1.5">
                {renderMatchBadge(activity.matchTypes)}
              </div>
            )}
          </div>
          <Info className="w-4 h-4 text-gray-400 group-hover:text-morado transition-colors flex-shrink-0 mt-0.5" />
        </div>
      </motion.button>
    );
  };

  return (
    <>
      {/* Sección principal colapsable */}
      <div className="mt-4 rounded-2xl border border-[#d4af37]/40 overflow-hidden bg-white/90 backdrop-blur-sm">
        {/* Header colapsable */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-morado flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-morado text-sm">
                Actividades ideales para hoy
              </div>
              <div className="text-xs text-gray-500">
                {matches.tripleMatch.length > 0 && (
                  <span className="text-amber-600 font-medium">{matches.tripleMatch.length} óptimas • </span>
                )}
                {matches.doubleMatch.length > 0 && (
                  <span className="text-morado">{matches.doubleMatch.length} recomendadas • </span>
                )}
                <span>{matches.singleMatch.length} sugeridas</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-400 hidden sm:block">
              DU:{universalDay} • DP:{personalDay} • MU:{universalMonth}
            </div>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </button>

        {/* Contenido expandible */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-4">
                
                {/* Triple Match - Actividades Óptimas */}
                {matches.tripleMatch.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      </div>
                      <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                        Óptimas (Triple coincidencia)
                      </span>
                    </div>
                    <div className="space-y-2">
                      {matches.tripleMatch.map(activity => renderActivityItem(activity))}
                    </div>
                  </div>
                )}

                {/* Double Match - Recomendadas */}
                {matches.doubleMatch.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        <Star className="w-4 h-4 text-morado fill-morado" />
                        <Star className="w-4 h-4 text-morado fill-morado" />
                      </div>
                      <span className="text-xs font-semibold text-morado uppercase tracking-wide">
                        Recomendadas (Doble coincidencia)
                      </span>
                    </div>
                    <div className="space-y-2">
                      {matches.doubleMatch.map(activity => renderActivityItem(activity))}
                    </div>
                  </div>
                )}

                {/* Single Match - Por categoría */}
                {matches.singleMatch.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-gray-500" />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Sugeridas por Día Universal {universalDay}
                      </span>
                    </div>
                    
                    {/* Tabs de categorías */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      <button
                        onClick={() => setActiveCategory(null)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          activeCategory === null
                            ? 'bg-morado text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        Todas ({matches.singleMatch.length})
                      </button>
                      {ACTIVITY_CATEGORIES.map(cat => {
                        const count = singlesByCategory[cat.id]?.length || 0;
                        if (count === 0) return null;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              activeCategory === cat.id
                                ? 'bg-morado text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {cat.icon} {count}
                          </button>
                        );
                      })}
                    </div>

                    {/* Lista filtrada - sin scroll, mostrar todas */}
                    <div className="space-y-2">
                      {(activeCategory 
                        ? singlesByCategory[activeCategory] || []
                        : matches.singleMatch
                      ).map(activity => renderActivityItem(activity, false))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modal de detalle de actividad */}
      <AnimatePresence>
        {selectedActivity && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
            onClick={() => setSelectedActivity(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-[#d4af37]"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div 
                className="px-5 py-4 text-white"
                style={{ 
                  background: getCategoryById(selectedActivity.category)?.color || 'var(--color-morado)'
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{getCategoryById(selectedActivity.category)?.icon}</span>
                    <div>
                      <div className="font-bold text-lg leading-tight">
                        {selectedActivity.name}
                      </div>
                      <div className="text-sm opacity-90">
                        {getCategoryById(selectedActivity.category)?.name}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedActivity(null)}
                    className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition flex items-center justify-center"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Contenido */}
              <div className="p-5 space-y-4">
                {/* Mejor día */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-morado flex items-center justify-center">
                    <span className="text-3xl font-bold text-white">{selectedActivity.bestDay}</span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Mejor Día Universal</div>
                    <div className="font-semibold text-morado">
                      Día {selectedActivity.bestDay}
                    </div>
                    {selectedActivity.matchTypes && (
                      <div className="mt-1">
                        {renderMatchBadge(selectedActivity.matchTypes)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Interpretación */}
                <div className="p-4 rounded-xl bg-morado/5 border border-morado/10">
                  <div className="text-sm font-semibold text-morado mb-2">
                    📖 Interpretación Numerológica
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {selectedActivity.interpretation}
                  </p>
                </div>

                {/* Metadatos adicionales */}
                {(selectedActivity.personalDays.length > 0 || 
                  selectedActivity.universalMonths.length > 0 || 
                  selectedActivity.universalWeeks.length > 0 ||
                  selectedActivity.masterNumbers.length > 0) && (
                  <div className="text-xs text-gray-500 space-y-1">
                    {selectedActivity.personalDays.length > 0 && (
                      <div>✨ Potenciado en días personales: {selectedActivity.personalDays.join(', ')}</div>
                    )}
                    {selectedActivity.universalMonths.length > 0 && (
                      <div>📅 Ideal en meses universales: {selectedActivity.universalMonths.join(', ')}</div>
                    )}
                    {selectedActivity.universalWeeks.length > 0 && (
                      <div>📆 Mejor en semanas universales: {selectedActivity.universalWeeks.join(', ')}</div>
                    )}
                    {selectedActivity.masterNumbers.length > 0 && (
                      <div>🌟 Combina con números maestros: {selectedActivity.masterNumbers.join(', ')}</div>
                    )}
                  </div>
                )}

                {/* Botón cerrar */}
                <button
                  onClick={() => setSelectedActivity(null)}
                  className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:shadow-lg"
                  style={{ 
                    background: 'var(--color-morado)'
                  }}
                >
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

